import i18n from '@/utils/i18n';
import { COMPLETE_TRANSLATION_FRAGMENT } from '@/hooks/fragments/translation-fragments';
import { CurrentOrderSection } from '@/enums';
import { formatSummaryDate, getDeviceTimezone } from '@/utils/date';
import {
  GetHubCurrentOrdersQuery,
  GetHubCurrentOrdersQueryVariables,
  HubCardType,
} from '@/types';
import { gql, NetworkStatus } from '@apollo/client';
import { HubCardDataOptions, useHubCardData } from './use-hub-card-data';
import { HubOrderOption, toOrderOption } from './order-option';
import { ListSection } from '@/components/recycler-list';
import { useCallback, useMemo, useState } from 'react';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

interface OrderSection {
  data: HubOrderOption[];
  key: CurrentOrderSection;
  metadata: { title: string };
}

export const GET_HUB_CURRENT_ORDERS_QUERY = gql`
  ${COMPLETE_TRANSLATION_FRAGMENT}

  query getHubCurrentOrders(
    $businessOrganizationUUID: uuid!
    $userTimezone: String!
  ) {
    getHubCurrentOrders(
      business_organization_id: $businessOrganizationUUID
      user_timezone: $userTimezone
    ) {
      created_at
      chat_id
      description {
        ...CompleteTranslationFields
      }
      is_call_sheet_order
      is_draft_order
      item_count_display {
        ...CompleteTranslationFields
      }
      operator_employees {
        name
        phone_number
        user_id
      }
      ordered_by_user_id
      ordered_by_user_phone_number
      order_amount_in_micros
      order_status
      order_id
      restaurant_name
      restaurant_name_i18n {
        ...CompleteTranslationFields
      }
      restaurant_id
      supplier_id
    }
  }
`;

export const useHubCardOrders = (options?: HubCardDataOptions) => {
  const { data, loading, error } = useHubCardData(HubCardType.CurrentOrders, {
    // use cache-and-network for current orders since the data is updated frequently
    fetchPolicy: options?.fetchPolicy ?? 'cache-and-network',
    skip: options?.skip ?? false,
  });

  return {
    data: data ? data.cardData[0] : undefined,
    error,
    loading,
  };
};

export const useCurrentOrders = (businessOrganizationUUID: string) => {
  const todayFormatted = useMemo(() => formatSummaryDate(new Date()), []);
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  const { data, loading, error, refetch, networkStatus } = useWrappedQuery<
    GetHubCurrentOrdersQuery,
    GetHubCurrentOrdersQueryVariables
  >(GET_HUB_CURRENT_ORDERS_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !businessOrganizationUUID,
    variables: {
      businessOrganizationUUID,
      userTimezone: getDeviceTimezone(),
    },
  });

  const orders = useMemo(() => data?.getHubCurrentOrders ?? [], [data]);

  const sectionedCurrentOrders = useMemo(() => {
    if (!orders.length) {
      return {
        all: [],
        scheduled: [],
        unscheduled: [],
      };
    }

    const scheduled = orders.filter((o) => o.is_call_sheet_order);
    const unscheduled = orders.filter((o) => !o.is_call_sheet_order);

    const sectionsFromOrders = (
      orders: GetHubCurrentOrdersQuery['getHubCurrentOrders'],
    ): ListSection<HubOrderOption, { title: string }>[] => {
      const sections: OrderSection[] = [];

      const draft = orders.filter((o) => o.is_draft_order);
      const placed = orders.filter((o) => o.is_draft_order === false);
      const expected = orders.filter((o) => o.is_draft_order === null);

      if (draft.length) {
        sections.push({
          data: draft.map(toOrderOption),
          key: CurrentOrderSection.Draft,
          metadata: {
            title: i18n.t('current_orders.draft_orders'),
          },
        });
      }
      if (placed.length) {
        sections.push({
          data: placed.map(toOrderOption),
          key: CurrentOrderSection.Placed,
          metadata: {
            title: i18n.t('current_orders.placed_orders'),
          },
        });
      }
      if (expected.length) {
        sections.push({
          data: expected.map(toOrderOption),
          key: CurrentOrderSection.CallSheet,
          metadata: {
            title: i18n.t('current_orders.expected_orders', {
              date: todayFormatted,
            }),
          },
        });
      }

      return sections;
    };

    return {
      all: sectionsFromOrders(orders),
      scheduled: sectionsFromOrders(scheduled),
      unscheduled: sectionsFromOrders(unscheduled),
    };
  }, [orders, todayFormatted]);

  const refresh = useCallback(
    (manual = false) => {
      (async () => {
        try {
          setIsManualRefresh(manual);
          await refetch();
        } finally {
          setIsManualRefresh(false);
        }
      })();
    },
    [refetch],
  );

  return {
    error,
    loading,
    refresh,
    refreshing: isManualRefresh && networkStatus === NetworkStatus.refetch,
    sectionedCurrentOrders,
  };
};
